import { useQuery } from '@tanstack/react-query';
import { Share2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { getCrossProjectIntelligence } from '@/lib/api';

/**
 * SharedPatternsPanel
 *
 * Shows categories appearing in 2+ projects
 */
export function SharedPatternsPanel(): React.ReactElement | null {
  const { data, isLoading } = useQuery({
    queryKey: ['intelligence'],
    queryFn: getCrossProjectIntelligence,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  if (isLoading) {
    return <div className="text-xs text-text-secondary">Loading shared patterns...</div>;
  }

  if (!data) {
    return null;
  }

  // Find categories appearing in 2+ projects
  const projectsByCategory = new Map<string, number>();
  for (const project of data.projects) {
    const categories = new Set<string>();
    for (const pattern of project.recentPatterns) {
      categories.add(pattern.category);
    }
    for (const category of categories) {
      projectsByCategory.set(category, (projectsByCategory.get(category) ?? 0) + 1);
    }
  }

  const shared = Array.from(projectsByCategory.entries())
    .filter(([, count]) => count >= 2)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);

  return (
    <div className="space-y-2">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-text-tertiary flex items-center gap-1">
        <Share2 className="h-3.5 w-3.5" />
        Shared Patterns
      </div>

      {shared.length > 0 ? (
        <div className="space-y-1">
          {shared.map(([category, count]) => (
            <div key={category} className="flex items-center gap-2 justify-between">
              <span className="text-xs text-text-secondary truncate">
                {category.replace(/_/g, ' ')}
              </span>
              <Badge variant="secondary" className="text-[9px]">
                {count}
              </Badge>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded bg-surface-elevated/50 p-2 text-center text-[10px] text-text-tertiary">
          No shared patterns yet
        </div>
      )}
    </div>
  );
}
